import { PlusOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Checkbox } from 'antd';
import { Landmark } from 'lucide-react';
import { useState } from 'react';
import { type InsurerAction, InsurerEdit } from './InsurerEdit';

export const Route = createFileRoute('/console/$orgId/configuration/claims-products/insurers')({
    staticData: {
        metadata: {
            title: 'Insurers',
        },
        ui: {
            icon: Landmark,
            title: 'Insurers configuration',
            subtitle: 'Manage insurer details and contacts',
            menuDescription: 'Manage insurer details and contacts',
        },
        modules: ['claim'],
        permissions: ['settings:update'],
    },
    component: ConfigurationInsurers,
});

type QueryParams = RouterInputs['insurer']['listInsurers'];
type InsurerRow = RouterOutputs['insurer']['listInsurers']['rows'][number];
type InsurerOrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<InsurerRow, InsurerOrderBy>();

function ConfigurationInsurers() {
    const [action, setAction] = useState<InsurerAction>(['none']);
    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: 10,
        offset: 0,
        orderBy: [],
    });

    const { data, isLoading, refetch } = trpc.insurer.listInsurers.useQuery(queryParams);
    const { mutateAsync: deleteInsurer } = trpc.insurer.deleteInsurer.useMutation();

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => String(value),
            align: 'center',
        }),
        columnHelper.accessor('name', {
            id: 'name',
            header: 'Name',
            sortable: 'name',
        }),
        columnHelper.accessor('email', {
            id: 'email',
            header: 'E-mail',
            sortable: 'email',
        }),
        columnHelper.accessor('applyGst', {
            id: 'apply-gst',
            header: 'Apply GST?',
            cell: ({ value }) => <Checkbox checked={!!value} onChange={() => {}} />,
        }),
        columnHelper.actions({
            cell: ({ row }) => (
                <Button.Delete
                    onConfirm={async (callback) => {
                        try {
                            await deleteInsurer({ id: row.id });
                            callback();
                            void refetch();
                        } catch (e) {
                            errorMessage.show(e);
                        }
                    }}
                />
            ),
        }),
    ];

    const tableProps = usePrismaTable<InsurerRow, QueryParams['where'], InsurerOrderBy>({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: { position: 'both' },
    });

    return (
        <PageController route={Route}>
            <InsurerEdit
                action={action}
                onClose={() => {
                    setAction(['none']);
                    void refetch();
                }}
            />

            <TableToolbar
                filters={undefined}
                actions={
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAction(['add'])}>
                        Add new
                    </Button>
                }
            />

            <PrismaTable
                {...tableProps}
                size="small"
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick: () => setAction(['edit', id]),
                })}
            />
        </PageController>
    );
}
